import React, {ReactElement, useCallback, useEffect, useMemo, useRef, useState} from "react";
import * as http from "../../../handlers/http";
import {APIKey} from "../../../types/APIKey";
import ZeeroLoading from "../../custom/ZeeroLoading";
import {ColumnDef, ZeeroDataTable} from "../../custom/ZeeroDataTable";
import ZeeroCheck from "../../custom/ZeeroCheck";
import ZeeroButton from "../../custom/ZeeroButton";
import {faTrash, faTriangleExclamation} from "@awesome.me/kit-92187fb46c/icons/classic/solid";
import {Body, Content, Footer, Prefix, Title, ZeeroPopup} from "../../custom/ZeeroPopup";
import ZeeroDatePicker, {ZEERO_DATE_FORMAT} from "../../custom/ZeeroDatePicker";
import {faCopy} from "@awesome.me/kit-92187fb46c/icons/classic/regular";
import Clipboard from "../../../handlers/Clipboard";
import moment, {Moment} from "moment";
import {toast} from "../../custom/toasters/ToastersManager";
import {useTranslation} from "react-i18next";

const {FontAwesomeIcon} = await import("@fortawesome/react-fontawesome");

type APIKeyCreationRecap = {
	name?: string,
	expiration?: string | null,
	token?: string
}

export default function APIKeysPage(): ReactElement {
	const {t} = useTranslation();
	const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [showCreationPopup, setShowCreationPopup] = useState<boolean>(false);
	const [showDeletionPopup, setShowDeletionPopup] = useState<{ show: boolean, name: string | null }>({
		show: false,
		name: null
	});
	const [creationRecapPopup, setCreationRecapPopup] = useState<{
		show: boolean
	} & APIKeyCreationRecap>({show: false});

	const load = useCallback(async () => {
		try {
			const res = await http.get<{ result: APIKey[] }>("/web/admin/api-keys");
			setApiKeys(res.result);
		} catch (e) {
			toast.error(t("apiKeys_errorLoadingAPIKeys"));
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => {
		void load();
	}, []);

	const onCreate = useCallback(async (name: string, expiration?: string) => {
		try {
			const res = await http.post<{ name: string; expiration: string | null; token: string }>(
					"/web/admin/api-keys",
					{name, expiration}
			);
			await load();
			setShowCreationPopup(false)
			setCreationRecapPopup({show: true, ...res});
		} catch (e) {
			toast.error(t("apiKeys_errorCreatingAPIKey"));
			setShowCreationPopup(false)
		}
	}, [load]);

	const toggleAPIKey = useCallback(async (name: string, enabled: boolean) => {
		const backup = apiKeys;
		setApiKeys(prev => prev.map(k => k.name === name ? {...k, enabled} : k));

		try {
			await http.patch("/web/admin/api-keys", {name, enabled});
			await load();
		} catch (e) {
			toast.error(t("apiKeys_errorUpdatingAPIKey"));
			setApiKeys(backup);
		}
	}, [apiKeys, load]);

	const onDelete = useCallback(async (name: string) => {
		const backup = apiKeys;
		setApiKeys(prev => prev.filter(k => k.name !== name));

		try {
			await http.del("/web/admin/api-keys", {name});
			await load();
			setShowDeletionPopup({show: false, name: null});
		} catch (e) {
			toast.error(t("apiKeys_errorDeletingAPIKey"));
			setApiKeys(backup);
			setShowDeletionPopup({show: false, name: null});
		}
	}, [apiKeys, load]);

	const columns: ColumnDef<APIKey>[] = useMemo(() => [
		{
			id: "name",
			header: t("apiKeys_name"),
			getValue: (a) => a.name,
			cell: ({row}) => <span>{row.name}</span>,
			size: "2fr"
		},
		{
			id: "createdAt",
			header: t("apiKeys_createdAt"),
			getValue: (a) => a.createdAt,
			cell: ({row}) => <span>{row.createdAt}</span>
		},
		{
			id: "lastUsed",
			header: t("apiKeys_lastUsed"),
			getValue: (a) => a.lastUsed,
			cell: ({row}) => <span>{row.lastUsed}</span>
		},
		{
			id: "expiration",
			header: t("apiKeys_expiration"),
			getValue: (a) => a.expiration,
			cell: ({row}) => <span>{row.expiration ?? "-"}</span>
		},
		{
			id: "enabled",
			header: t("apiKeys_enabled"),
			getValue: (a) => a.enabled,
			cell: ({row, value, onChange}) => (
					<ZeeroCheck id={`api-key-check-${row.name}`} checked={value} onChange={onChange}/>
			)
		},
		{
			id: "deleteAction",
			header: "",
			getValue: () => null,
			cell: ({row}) => (
					<ZeeroButton type="none" onClick={() => setShowDeletionPopup({show: true, name: row.name})}>
						<FontAwesomeIcon icon={faTrash}/>
					</ZeeroButton>
			)
		}
	], []);

	if (loading) {
		return (
				<div className="page-content">
					<div className="page-title">{t("apiKeys_apiKeys", {count: 3})}</div>
					<ZeeroLoading className="configurations" message={t("apiKeys_loading")}/>
				</div>
		);
	}

	return (
			<div className="page-content">
				<div className="page-title">{t("apiKeys_apiKeys", {count: 3})}</div>

				<ZeeroDataTable<APIKey>
						rows={apiKeys}
						getRowId={(a) => a.name}
						columns={columns}
						onValueChange={(rowId, _, next) => toggleAPIKey(rowId, next)}
						maxHeight={"75vh"}
				/>

				<div className="data-footer actions">
					<ZeeroButton style={{width: "7.5em"}} type="primary" onClick={() => setShowCreationPopup(true)}>
						{t("general_create")}
					</ZeeroButton>
				</div>

				{showCreationPopup &&
						<CreateAPIKeyPopup onSave={onCreate} onClose={() => setShowCreationPopup(false)}/>
				}

				{creationRecapPopup.show &&
						<RecapAPIKeyPopup recap={creationRecapPopup} onClose={() => setCreationRecapPopup({
							show: false,
							name: undefined,
							expiration: null,
							token: undefined
						})}/>
				}

				{showDeletionPopup.show && showDeletionPopup.name &&
						<DeleteAPIKeyPopup
								name={showDeletionPopup.name}
								onDelete={onDelete}
								onClose={() => setShowDeletionPopup({show: false, name: null})}
						/>
				}
			</div>
	);
}

const CreateAPIKeyPopup = ({onSave, onClose}: {
	onSave: (name: string, expiration: string | undefined) => void,
	onClose: () => void
}): ReactElement => {
	const {t} = useTranslation();
	const nameRef = useRef<HTMLInputElement>(null);
	const [name, setName] = useState<string | undefined>(undefined);
	const [expireDay, setExpireDay] = useState<Moment | null>(null);

	const handleSave = () => {
		const nameValid = name != undefined && name != ""
		if (!nameValid) {
			nameRef.current?.classList.add("invalid");
		}

		const expirationValid = expireDay == undefined || expireDay.isValid();

		if (!nameValid || !expirationValid) {
			return;
		}

		nameRef.current?.classList.remove("invalid");
		onSave(name, expireDay?.format(ZEERO_DATE_FORMAT));
	};

	return (
			<ZeeroPopup className="create-api-key-popup">
				<Content>
					<Title>{t("apiKeys_createPopupTitle")}</Title>
					<Body>
						<div>{t("apiKeys_createPopupBody")}</div>
						<div className="api-key-form">
							<div>{t("apiKeys_name")}</div>
							<input
									ref={nameRef}
									type="text"
									className="zeero-input"
									onChange={e => setName(e.target.value)}
							/>
							<div>{t("apiKeys_expiration")} ({t("general_optional")})</div>
							<ZeeroDatePicker
									id="create-api-key-date-picker"
									selectedDay={expireDay}
									setSelectedDay={setExpireDay}
									allowedRange={{min: moment(new Date()).add(1, "d"), max: moment(new Date(2500, 1))}}
							/>
						</div>
					</Body>
				</Content>
				<Footer>
					<ZeeroButton size={"xs"} type="secondary" onClick={onClose}>
						{t("general_cancel")}
					</ZeeroButton>
					<ZeeroButton size={"xs"} type="primary" onClick={handleSave}>
						{t("general_create")}
					</ZeeroButton>
				</Footer>
			</ZeeroPopup>
	)
}

const RecapAPIKeyPopup = ({recap, onClose}: { recap: APIKeyCreationRecap, onClose: () => void }):
		ReactElement => {
	const {t} = useTranslation();

	const copyToClipboard = async () => {
		await Clipboard.copyToClipboard(recap.token!);
	}

	return (
			<ZeeroPopup className="recap-api-key-popup">
				<Content>
					<Title>{t("apiKeys_recapPopupTitle")}</Title>
					<Body>
						<div><FontAwesomeIcon icon={faTriangleExclamation} color="var(--warn)"/> {t("apiKeys_recapPopupBody")}</div>
						<div className="api-key-form">
							<div>{t("apiKeys_name")}</div>
							<div className="recap-cell">{recap.name}</div>
							<div>{t("apiKeys_expiration")}</div>
							<div className="recap-cell">{recap.expiration ?? <i>{t("general_notSet")}</i>}</div>
							<div>{t("apiKeys_apiKeys", {count: 1})}</div>
							<div className="recap-token-cell">
								<div className="recap-cell">{recap.token}</div>
								<ZeeroButton type="table-action" onClick={copyToClipboard}>
									<FontAwesomeIcon icon={faCopy}/>
								</ZeeroButton>
							</div>
						</div>
					</Body>
				</Content>
				<Footer>
					<ZeeroButton size={"xs"} type="secondary" onClick={onClose}>
						{t("general_close")}
					</ZeeroButton>
				</Footer>
			</ZeeroPopup>
	)
}

const DeleteAPIKeyPopup = ({name, onDelete, onClose}: {
	name: string,
	onDelete: (name: string) => void,
	onClose: () => void
}): ReactElement => {
	const {t} = useTranslation();

	return (
			<ZeeroPopup className="delete-api-key-popup">
				<Prefix>
					<FontAwesomeIcon icon={faTriangleExclamation} size="2xl" style={{color: "var(--danger)"}}/>
				</Prefix>
				<Content>
					<Title>{t("apiKeys_deletePopupTitle")}</Title>
					<Body>
						<div>{t("apiKeys_deletePopupBody", {name})}</div>
					</Body>
				</Content>
				<Footer>
					<ZeeroButton size={"xs"} type="secondary" onClick={onClose}>
						{t("general_cancel")}
					</ZeeroButton>
					<ZeeroButton size={"xs"} type="danger" onClick={() => onDelete(name)}>
						{t("general_delete")}
					</ZeeroButton>
				</Footer>
			</ZeeroPopup>
	)
}
